/**
 * The plan on screen, section for section with the PDF.
 *
 * Every section here is the same section the document prints, in the same
 * order, with the same figures and the same headings — because both read the
 * spine from `buildSkeleton` and both take their content from
 * `helpers/portfolioPlanner/reportContent`. Neither surface decides what a
 * section contains, which is what stops the two drifting apart. They used to,
 * and the screen ended up telling a different story from the document the
 * client was handed.
 *
 * What differs is only what has to: the PDF draws with jsPDF primitives, this
 * renders JSX. Ported from the user platform's ReportSections.tsx — same
 * renderers, same dispatch — with the admin's indigo palette in place of the
 * client theme, MUI icons for iconsax, and the temporary backend-mapping
 * bubbles dropped (they are a user-platform authoring aid with no counterpart
 * in an operations console).
 */

import React from "react";
import {
  adequacyBars,
  adequacyTable,
  assumptionsTable,
  bondHeader,
  cashflowTable,
  deploymentLede,
  deploymentTiles,
  fundsTable,
  gapParts,
  glanceTiles,
  goalFacts,
  goalsOverviewLede,
  goalsOverviewTable,
  inr,
  liabilityParts,
  monthlyTable,
  orderTables,
  planCoversTable,
  retirementParts,
  setWorking,
  settlementLedger,
  sweepParts,
  taxTable,
  tracesByPart,
} from "../../../Helpers/portfolioPlanner/reportContent";
import type {
  FactRow,
  LedgerRow,
  ReportContext,
  StatTile,
  TableSpec,
} from "../../../Helpers/portfolioPlanner/reportContent";
import { rollUp } from "../../../Helpers/portfolioPlanner/report/blocks";
import type { Block, Section } from "../../../Helpers/portfolioPlanner/report/blocks";
import type { PlannerConfig } from "../../../Helpers/portfolioPlanner/types";
import { ChartFrame, CorpusArea } from "./PlannerCharts";
import ReportBlocks from "./ReportBlocks";
import { EligibleBondsTable } from "./ReportBits";
import styles from "./portfolio-planner.module.css";

/* ───────────────────────────── primitives ───────────────────────────── */

/** The band every section opens on — "PART ONE · SECTION 03". */
export const SectionBand = ({ section }: { section: Section }) => {
  const kind = /^[A-Z]$/.test(section.number) ? "APPENDIX" : "SECTION";
  const label = [section.part, section.number ? `${kind} ${section.number}` : ""]
    .filter(Boolean)
    .join("  ·  ");
  return (
    <div className={styles.band}>
      {label ? <span className={styles.bandLabel}>{label}</span> : null}
      <h4 className={styles.bandTitle}>{section.title}</h4>
    </div>
  );
};

/** "PART ONE · Retirement" — which of the four things you are inside. */
export const PartOpener = ({ part, name }: { part: string; name: string }) => (
  <div className={styles.partOpener}>
    <span>{part}</span>
    <b>{name}</b>
  </div>
);

export const Lede = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.lede}>{children}</p>
);

export const StatStrip = ({
  tiles,
  compact,
}: {
  tiles: StatTile[];
  compact?: boolean;
}) => (
  <div className={`${styles.strip} ${compact ? styles.stripCompact : ""}`}>
    {tiles.map((tile) => (
      <div
        key={tile.label}
        className={`${styles.stripTile} ${tile.flagged ? styles.stripTileFlagged : ""}`}
      >
        <span>{tile.label}</span>
        <b>{tile.value}</b>
        {tile.caption ? <i>{tile.caption}</i> : null}
      </div>
    ))}
  </div>
);

/** Full-width label/value rows; the last is the figure the section works to. */
export const FactList = ({ rows }: { rows: FactRow[] }) => (
  <dl className={styles.factList}>
    {rows.map((row, index) => (
      <div
        key={row.label}
        className={index === rows.length - 1 ? styles.factRowLast : undefined}
      >
        <dt>{row.label}</dt>
        <dd>{row.value}</dd>
      </div>
    ))}
  </dl>
);

/** The running subtraction — a rule above the total, which is set heavier. */
export const Ledger = ({
  rows,
  caption,
}: {
  rows: LedgerRow[];
  caption?: string;
}) => (
  <div className={styles.ledger}>
    {caption ? <span className={styles.ledgerCaption}>{caption}</span> : null}
    {rows.map((row, index) => (
      <div
        key={row.label}
        className={index === rows.length - 1 ? styles.ledgerTotal : undefined}
      >
        <span>
          {row.label}
          {row.note ? <i>{row.note}</i> : null}
        </span>
        <b className={row.negative ? styles.negative : undefined}>{row.value}</b>
      </div>
    ))}
  </div>
);

export const SpecTable = ({ spec }: { spec: TableSpec }) => (
  <div className={styles.tableWrap}>
    <table className={styles.table}>
      {spec.caption ? <caption>{spec.caption}</caption> : null}
      <thead>
        <tr>
          {spec.columns.map((column) => (
            <th
              key={column.header}
              style={{
                width: `${column.width}%`,
                textAlign: column.align ?? "left",
              }}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {spec.rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((c, index) => (
              <td
                key={index}
                style={{ textAlign: spec.columns[index]?.align ?? "left" }}
                className={[
                  c.strong ? styles.strong : "",
                  c.muted || spec.columns[index]?.subtle ? styles.subtle : "",
                  c.alarm ? styles.negative : "",
                  spec.columns[index]?.align === "right" ? styles.numeric : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {c.text}
                {c.note ? <i className={styles.formula}>{c.note}</i> : null}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {spec.footnote ? (
      <p className={styles.tableFootnote}>{spec.footnote}</p>
    ) : null}
  </div>
);

/**
 * What the plan builds, against what each horizon requires.
 *
 * THE BAR CHARTS THE SHORTFALL, NOTHING ELSE.
 *
 * Two earlier versions charted the totals — stacked against the plan's corpus,
 * then against it as a benchmark line — and both were confusing for the same
 * reason: the quantity that matters is a difference, and a reader had to
 * recover it by comparing two lengths. Charting the difference directly removes
 * the step. One series, one colour, every bar from the same zero, immediately
 * beside the figure it encodes.
 *
 * That also settles the division of labour on this page: the band states what
 * you have, the columns give exact figures, and the bar shows the one thing a
 * column cannot — how fast the gap opens as the horizon lengthens.
 */
export const AdequacyGap = ({
  caption,
  rows,
}: {
  caption: string;
  rows: {
    label: string;
    value: string;
    shortfall: number;
    multiple: number | null;
    reference: boolean;
  }[];
}) => {
  const built = rows.find((row) => row.reference);
  const horizons = rows.filter((row) => !row.reference);
  const worst = Math.max(...horizons.map((row) => row.shortfall), 1);

  return (
    <div className={styles.gapBlock}>
      <span className={styles.ledgerCaption}>{caption}</span>

      {built ? (
        <div className={styles.gapBuilt}>
          <span>{built.label}</span>
          <b>{built.value}</b>
        </div>
      ) : null}

      <div className={styles.gapRows}>
        <div className={styles.gapHead}>
          <span>If the corpus must last</span>
          <span>It needs</span>
          <span className={styles.gapHeadShort}>You are short by</span>
        </div>
        {horizons.map((row) => (
          <div key={row.label} className={styles.gapRow}>
            <span>
              {row.label}
              {row.multiple != null && row.multiple > 1 ? (
                <i>{row.multiple.toFixed(1)}× what you have</i>
              ) : null}
            </span>
            <b>{row.value}</b>
            <i className={styles.gapBarCell}>
              <u
                className={styles.gapBar}
                style={{ width: `${Math.max(3, (row.shortfall / worst) * 100)}%` }}
              />
            </i>
            <b className={styles.gapShort}>{inr(row.shortfall)}</b>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────── section renderers ──────────────────────── */

interface SectionProps {
  ctx: ReportContext;
  section: Section;
  blocks: Block[];
  demoted: Block[];
  sections: Section[];
  config?: PlannerConfig;
}

const Findings = ({
  blocks,
  sections,
  keys,
  subjectId,
}: {
  blocks: Block[];
  sections: Section[];
  keys: Section["key"] | Section["key"][];
  subjectId?: number | string;
}) => (
  <ReportBlocks
    blocks={blocks}
    sections={sections}
    section={keys}
    subjectId={subjectId}
  />
);

const Glance = ({ ctx, blocks, sections }: SectionProps) => (
  <>
    <Findings blocks={blocks} sections={sections} keys="glance" />
    <StatStrip tiles={glanceTiles(ctx)} />
    {/* What the rest of the document found, de-duplicated and capped. Never
        hand-written, or the summary starts contradicting the sections. */}
    <ReportBlocks blocks={rollUp(blocks)} sections={sections} />
    <SpecTable spec={planCoversTable(ctx)} />
  </>
);

const GoalsOverview = ({ ctx, blocks, sections }: SectionProps) => {
  const lede = goalsOverviewLede(ctx);
  return (
    <>
      <Findings blocks={blocks} sections={sections} keys="goals-overview" />
      {lede ? <Lede>{lede}</Lede> : null}
      <SpecTable spec={goalsOverviewTable(ctx)} />
    </>
  );
};

const Goal = ({ ctx, section, blocks, sections }: SectionProps) => {
  const index = ctx.plan.goals.findIndex((goal) => goal.id === section.subjectId);
  if (index < 0) return null;
  const goal = ctx.plan.goals[index];

  /* A SIP-only goal has no sections of its own for the gap or the basket — the
     skeleton gives it one number — so both are drawn inside it, exactly as the
     document does. */
  const ownsGap = sections.some(
    (s) => s.key === "gap" && s.subjectId === goal.id
  );
  const ownsFunds = sections.some(
    (s) => s.key === "goal-funds" && s.subjectId === goal.id
  );
  const gap = !ownsGap && goal.sip > 0 ? gapParts(ctx, index) : null;

  return (
    <>
      {section.part ? <PartOpener part={section.part} name={goal.name} /> : null}
      <FactList rows={goalFacts(ctx, goal)} />
      <Findings blocks={blocks} sections={sections} keys="goal" subjectId={goal.id} />
      {gap ? (
        <>
          {gap.ledger ? <Ledger rows={gap.ledger} /> : null}
          <StatStrip tiles={gap.tiles} />
          <Findings blocks={blocks} sections={sections} keys="gap" subjectId={goal.id} />
        </>
      ) : null}
      {!ownsFunds && goal.funds.length ? (
        <>
          <span className={styles.ledgerCaption}>
            Where it goes
          </span>
          <SpecTable spec={fundsTable(goal.funds, `${goal.sipFreq} ₹`)} />
          <Findings
            blocks={blocks}
            sections={sections}
            keys="goal-funds"
            subjectId={goal.id}
          />
        </>
      ) : null}
    </>
  );
};

const Deployment = ({ ctx, section, blocks, sections }: SectionProps) => {
  const goal = ctx.plan.goals.find((g) => g.id === section.subjectId);
  if (!goal?.bond) return null;
  const header = bondHeader(goal);
  const ledger = settlementLedger(goal);

  return (
    <>
      <Lede>{deploymentLede(ctx, goal)}</Lede>
      <div className={styles.bondCard}>
        <div className={styles.bondCardHead}>
          <b>
            {header.title}
          </b>
          <span>{header.meta}</span>
        </div>
        <StatStrip tiles={deploymentTiles(goal)} compact />
      </div>
      {ledger ? <Ledger rows={ledger} caption="What you actually pay" /> : null}
      {goal.eligibleBonds?.length ? (
        <EligibleBondsTable
          bonds={goal.eligibleBonds}
          lumpsum={goal.bond.invested + goal.bond.idle}
        />
      ) : null}
      <Findings
        blocks={blocks}
        sections={sections}
        keys="deployment"
        subjectId={goal.id}
      />
    </>
  );
};

const Sweep = ({ ctx, section, blocks, sections }: SectionProps) => {
  const goal = ctx.plan.goals.find((g) => g.id === section.subjectId);
  if (!goal?.bond) return null;
  const parts = sweepParts(ctx, goal);
  return (
    <>
      <Lede>{parts.lede}</Lede>
      <StatStrip tiles={parts.tiles} />
      <p className={styles.disclaimer}>{parts.totalLine}</p>
      <Ledger rows={parts.ledger} />
      <Findings blocks={blocks} sections={sections} keys="sweep" subjectId={goal.id} />
    </>
  );
};

const Gap = ({ ctx, section, blocks, sections }: SectionProps) => {
  const index = ctx.plan.goals.findIndex((g) => g.id === section.subjectId);
  if (index < 0) return null;
  const parts = gapParts(ctx, index);
  return (
    <>
      {parts.ledger ? <Ledger rows={parts.ledger} /> : null}
      <StatStrip tiles={parts.tiles} />
      <Findings
        blocks={blocks}
        sections={sections}
        keys="gap"
        subjectId={section.subjectId}
      />
    </>
  );
};

const GoalFunds = ({ ctx, section, blocks, sections }: SectionProps) => {
  const goal = ctx.plan.goals.find((g) => g.id === section.subjectId);
  if (!goal) return null;
  return (
    <>
      <SpecTable spec={fundsTable(goal.funds, `${goal.sipFreq} ₹`)} />
      <Findings
        blocks={blocks}
        sections={sections}
        keys="goal-funds"
        subjectId={goal.id}
      />
    </>
  );
};

const Liability = ({ ctx, section, blocks, sections }: SectionProps) => {
  const index = Number(section.subjectId);
  const liability = ctx.plan.liabilities[index];
  if (!liability) return null;
  const parts = liabilityParts(ctx, index);
  return (
    <>
      {section.part ? (
        <PartOpener part={section.part} name={liability.name} />
      ) : null}
      <Lede>{parts.lede}</Lede>
      <FactList rows={parts.facts} />
      <SpecTable spec={parts.sizing} />
      <Findings blocks={blocks} sections={sections} keys="liability" subjectId={index} />
    </>
  );
};

const LiabilityFunds = ({ ctx, section, blocks, sections }: SectionProps) => {
  const liability = ctx.plan.liabilities[Number(section.subjectId)];
  if (!liability) return null;
  return (
    <>
      <SpecTable spec={fundsTable(liability.funds, "Monthly ₹")} />
      <Findings
        blocks={blocks}
        sections={sections}
        keys="liability-funds"
        subjectId={Number(section.subjectId)}
      />
    </>
  );
};

const Retirement = ({ ctx, section, blocks, sections }: SectionProps) => {
  const swp = ctx.plan.swp;
  if (!swp) return null;
  const parts = retirementParts(ctx);
  return (
    <>
      {section.part ? (
        <PartOpener part={section.part} name="Retirement Income" />
      ) : null}
      <Lede>{parts.lede}</Lede>
      <FactList rows={parts.facts} />
      {swp.trajectory.length > 1 ? (
        <ChartFrame title="What happens to the corpus" height={230}>
          <CorpusArea
            data={swp.trajectory.map((point) => ({
              label: `Yr ${point.year}`,
              value: point.balance,
            }))}
          />
        </ChartFrame>
      ) : null}
      <Findings blocks={blocks} sections={sections} keys="retirement" />
    </>
  );
};

const WhatItTakes = ({ ctx, blocks, sections }: SectionProps) => {
  if (!ctx.plan.swp || !ctx.adequacy) return null;
  const bars = adequacyBars(ctx);
  return (
    <>
      <Lede>
        Two variables: how much you have when you stop working, and how much you
        take out. Both are shown below on the plan&apos;s own{" "}
        {Math.round(ctx.plan.swp.ret)}% return and {Math.round(ctx.plan.swp.infl)}%
        inflation assumptions.
      </Lede>
      <AdequacyGap caption={bars.caption} rows={bars.rows} />
      <SpecTable spec={adequacyTable(ctx)} />
      <Findings blocks={blocks} sections={sections} keys="what-it-takes" />
    </>
  );
};

const Monthly = ({ ctx, blocks, sections }: SectionProps) => (
  <>
    <SpecTable spec={monthlyTable(ctx)} />
    <Findings blocks={blocks} sections={sections} keys="monthly" />
  </>
);

const Orders = ({ ctx, blocks, sections }: SectionProps) => {
  const tables = orderTables(ctx);
  return (
    <>
      <Lede>{tables.lede}</Lede>
      {tables.oneOff ? <SpecTable spec={tables.oneOff} /> : null}
      {tables.sips ? <SpecTable spec={tables.sips} /> : null}
      <Findings blocks={blocks} sections={sections} keys="orders" />
    </>
  );
};

const Tax = ({ ctx, blocks, sections }: SectionProps) => (
  <>
    <SpecTable spec={taxTable(ctx)} />
    <Findings blocks={blocks} sections={sections} keys="tax" />
  </>
);

const TraceAppendix = ({ ctx }: SectionProps) => (
  <>
    <Lede>
      Every number in this plan, in the order it was derived. These are the
      planning engine&apos;s own workings, reproduced without alteration.
    </Lede>
    {tracesByPart(ctx).map((part) => (
      <div key={part.title} className={styles.tracePart}>
        <span className={styles.ledgerCaption}>{part.title}</span>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: "30%" }}>Step</th>
                <th style={{ width: "50%" }}>Working</th>
                <th style={{ width: "20%", textAlign: "right" }}>Result</th>
              </tr>
            </thead>
            <tbody>
              {part.trace.map((row, index) => (
                <tr key={`${row.label}-${index}`}>
                  <td className={styles.strong}>{row.label}</td>
                  <td className={styles.subtle}>{setWorking(row.formula)}</td>
                  <td className={`${styles.numeric} ${styles.strong}`}>
                    {row.value == null ? "—" : inr(row.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ))}
  </>
);

const CashflowAppendix = ({ ctx, config }: SectionProps) => {
  const cashflows = cashflowTable(ctx);
  const assumptions = assumptionsTable(ctx, config);
  return (
    <>
      {cashflows ? <SpecTable spec={cashflows} /> : null}
      {assumptions ? <SpecTable spec={assumptions} /> : null}
    </>
  );
};

const Overflow = ({ demoted, sections }: SectionProps) => (
  <>
    <Lede>
      These were set aside to keep the sections above readable. They are not less
      true — only less urgent than what was shown alongside them.
    </Lede>
    <ReportBlocks blocks={demoted} sections={sections} />
  </>
);

const Assumptions = ({ ctx, config }: SectionProps) => {
  const spec = assumptionsTable(ctx, config);
  return spec ? <SpecTable spec={spec} /> : null;
};

/* ────────────────────────────── the dispatch ────────────────────────── */

const RENDERERS: Partial<
  Record<Section["key"], (props: SectionProps) => React.ReactElement | null>
> = {
  glance: Glance,
  "goals-overview": GoalsOverview,
  goal: Goal,
  deployment: Deployment,
  sweep: Sweep,
  gap: Gap,
  "goal-funds": GoalFunds,
  liability: Liability,
  "liability-funds": LiabilityFunds,
  retirement: Retirement,
  "what-it-takes": WhatItTakes,
  monthly: Monthly,
  orders: Orders,
  tax: Tax,
  "appendix-trace": TraceAppendix,
  "appendix-cashflows": CashflowAppendix,
  "appendix-overflow": Overflow,
  "appendix-assumptions": Assumptions,
};

/** One section of the document. The cover has no on-screen equivalent. */
export const ReportSection = (props: SectionProps) => {
  const Renderer = RENDERERS[props.section.key];
  if (!Renderer) return null;
  const body = Renderer(props);
  if (!body) return null;
  return (
    <section className={styles.section}>
      <SectionBand section={props.section} />
      {body}
    </section>
  );
};

export default ReportSection;
